<template>
  <!--
    The challenge's goals: the taxonomy values it is filed under, across five taxonomies at once.
    One searchable multi-select rather than five fields, because a reader of the challenge sees
    them as a single list of goals and the matching engine treats them the same way.
  -->
  <form-group
    :label="$t('challenge_goal_of_the_challenge', { challenge: challengeLabel })"
    :errors="errors"
    required
  >
    <Dropdown
      :options="goalOptions"
      :model-value="selectedKeys"
      :placeholder="$t('challenge_manage_goals_placeholder')"
      is-simplified
      multiple
      search
      @update:model-value="handleChange"
    />
    <!--
      Under the field, not in FormGroup's `instructions` slot: that slot sits beside the label in
      a flex row, so anything longer than a few words squeezes the label and wraps itself to one
      word a line.
    -->
    <p class="m-0 mt-1.5 text-xs leading-relaxed text-slate-500">
      {{ $t('challenge_manage_goals_help', { challenge: challengeLabel }) }}
    </p>
  </form-group>
</template>

<script>
  import isNumber from 'lodash/isNumber'
  import isObject from 'lodash/isObject'

  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'

  /**
   * The five taxonomies a challenge's goals are drawn from.
   *
   * `field` is the key on the challenge, `taxonomy` the key the label translations are filed
   * under, and `children` the property that holds a value's sub-values — the taxonomies each name
   * it after themselves, apart from technologies.
   */
  const GOAL_TAXONOMIES = [
    { field: 'activities', taxonomy: 'activities', state: 'activities', children: null, labelKey: 'actor_edit_activities' },
    { field: 'industries', taxonomy: 'industries', state: 'industries', children: 'industries', labelKey: 'actor_edit_industries' },
    { field: 'technology', taxonomy: 'technologies', state: 'technologies', children: 'children', labelKey: 'actor_edit_technology' },
    { field: 'domains', taxonomy: 'domains', state: 'domains', children: 'domains', labelKey: 'actor_edit_domains' },
    { field: 'stages', taxonomy: 'stages', state: 'stages', children: null, labelKey: 'manage_challenge_subtitle_growth_stage' },
  ]

  /**
   * The id of a saved taxonomy value, whichever of the three shapes it was written in: the API
   * returns `{ id }`, older clients wrote `{ value: id }` and `{ value: { id } }`. The backend
   * still reads all three (ChallengeTaxonomyTrait), so all three can be in the data.
   */
  function taxonomyId (value) {
    if (isNumber(value)) {
      return value
    }

    if (!isObject(value)) {
      return null
    }

    if (isNumber(value.id)) {
      return value.id
    }

    return (isObject(value.value) ? value.value.id : value.value) || null
  }

  export default {
    name: 'TaxonomyQuickEditChallenges',
    mixins: [TranslationsMixin],
    components: { Dropdown, FormGroup },
    props: {
      /** The challenge being edited. Only its five taxonomy arrays are read. */
      model: {
        type: Object,
        required: true,
      },
      errors: {
        type: [Array, String],
        default: null,
      },
    },
    emits: ['update:taxonomies'],
    computed: {
      /**
       * One entry per taxonomy, each with the values it offers. `name` is what gets stored,
       * `path` is what the dropdown shows — a sub-value's own name ("Dairy") means little without
       * the value it sits under, and the five taxonomies are searched as one list here.
       */
      goalGroups () {
        return GOAL_TAXONOMIES
          .map(group => ({
            field: group.field,
            label: this.$t(group.labelKey).trim(),
            items: (this.$store.state.taxonomies[group.state] || []).flatMap(value => {
              const name = this.taxonomyName(group.taxonomy, value)
              const children = (group.children && value[group.children]) || []

              return [
                { id: value.id, name, path: name },
                ...children.map(child => {
                  const childName = this.taxonomyName(group.taxonomy, child)

                  return { id: child.id, name: childName, path: `${name} › ${childName}` }
                }),
              ]
            }).filter(item => item.id && item.name),
          }))
          .filter(group => group.items.length)
      },
      /** Grouped options in the shape Dropdown expects: a label with `children` becomes a group. */
      goalOptions () {
        return this.goalGroups.map(group => ({
          label: group.label,
          children: group.items.map(item => ({
            value: this.optionKey(group.field, item.id),
            label: item.path,
          })),
        }))
      },
      /** Option key → what to store for it. Also the set of values this editor can resolve. */
      goalIndex () {
        const index = {}

        this.goalGroups.forEach(group => group.items.forEach(item => {
          index[this.optionKey(group.field, item.id)] = {
            field: group.field,
            id: item.id,
            label: item.name,
          }
        }))

        return index
      },
      selectedKeys () {
        return GOAL_TAXONOMIES
          .flatMap(group => (this.model[group.field] || [])
            .map(value => this.optionKey(group.field, taxonomyId(value))))
          // A value whose taxonomy the ecosystem has since dropped has no option to select, and
          // showing it would print the raw key. It is preserved on save — see handleChange.
          .filter(key => !!this.goalIndex[key])
      },
    },
    methods: {
      /** Taxonomy ids only have to be unique within their own taxonomy, so the field comes along. */
      optionKey (field, id) {
        return id ? `${field}:${id}` : ''
      },
      taxonomyName (taxonomy, value) {
        return this.getLabelForTaxonomyValue(taxonomy, value.id) || value.name || ''
      },
      handleChange (keys) {
        // Clearing a multi-select emits null rather than an empty array — see Dropdown.clear().
        const selected = Array.isArray(keys) ? keys : []
        const payload = {}

        GOAL_TAXONOMIES.forEach(group => {
          // Values this editor cannot resolve to an option are carried through untouched, so an
          // unrelated edit never silently drops them.
          payload[group.field] = (this.model[group.field] || [])
            .filter(value => !this.goalIndex[this.optionKey(group.field, taxonomyId(value))])
        })

        selected.forEach(key => {
          const option = this.goalIndex[key]

          if (option) {
            payload[option.field].push({ id: option.id, label: option.label })
          }
        })

        this.$emit('update:taxonomies', payload)
      },
    },
  }
</script>
